import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { logAuditEvent } from './services/AuditService';
import { shouldAuditLogin, clearLoginAuditMarker } from './utils/loginAuditDedupe';
import { showToast } from './components/ui/Toast';
import type { Session } from '@supabase/supabase-js';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './services/supabaseClient';
// ── Static imports — needed before/outside the route tree ────────────────────
import RoleSelector           from './pages/RoleSelector';
import BetaTerms              from './pages/BetaTerms';
import BetaExpired            from './pages/BetaExpired';
import PrivacyPolicy          from './pages/PrivacyPolicy';
import WaitlistPendingScreen  from './pages/WaitlistPendingScreen';
// ── Infrastructure (layout, providers, error boundary) ──────────────────────
import { SyncProvider }          from './context/SyncContext';
import { CompanyProvider }       from './context/CompanyContext';
import { AlertsProvider }        from './context/AlertsContext';
import { UploadSessionProvider } from './context/UploadSessionContext';
import { UserProvider }          from './context/UserContext';
import { GlobalLoadingProvider } from './context/GlobalLoadingContext';
import { LangProvider }          from './context/LangContext';
import { useUserRole }           from './context/UserRoleContext';
import { useCompanyContext }     from './context/CompanyContext';
import Layout        from './components/layout/Layout';
import ErrorBoundary from './components/shared/ErrorBoundary';
// ── Lazy page imports — each becomes its own JS chunk ─────────────────────────
// Login es lazy (P-3, audit 2026-07-06): arrastra @supabase/auth-ui-react
// (~156KB) que las sesiones ya autenticadas nunca necesitan.
const Login                = lazy(() => import('./pages/Login'));
const Upload               = lazy(() => import('./pages/Upload'));
const Dashboard            = lazy(() => import('./pages/Dashboard'));
const Balance              = lazy(() => import('./pages/Balance'));
const PyG                  = lazy(() => import('./pages/PyG'));
const FlujoCaja            = lazy(() => import('./pages/FlujoCaja'));
const CierreDual           = lazy(() => import('./pages/CierreDual'));
const JournalManual        = lazy(() => import('./pages/JournalManual'));
const LibroDiario          = lazy(() => import('./pages/LibroDiario'));
const LibroMayor           = lazy(() => import('./pages/LibroMayor'));
const SumasSaldos          = lazy(() => import('./pages/SumasSaldos'));
const Compras              = lazy(() => import('./pages/Compras'));
const Ventas               = lazy(() => import('./pages/Ventas'));
const ImpuestosIGV         = lazy(() => import('./pages/ImpuestosIGV'));
const PDT621               = lazy(() => import('./pages/PDT621'));
const CategorizadorRuc     = lazy(() => import('./pages/CategorizadorRuc'));
const Tributaria           = lazy(() => import('./pages/Tributaria'));
const Biblioteca           = lazy(() => import('./pages/Biblioteca'));
const AccountantConsole    = lazy(() => import('./pages/AccountantConsole'));
const ClientesView         = lazy(() => import('./pages/ClientesView'));
const Audit                = lazy(() => import('./pages/Audit'));
const Setup                = lazy(() => import('./pages/Setup'));
const BankInbox            = lazy(() => import('./pages/BankInbox'));
const InboxFallidos        = lazy(() => import('./pages/InboxFallidos'));
const Welcome              = lazy(() => import('./pages/Welcome'));
const LibroDiarioPle       = lazy(() => import('./pages/LibroDiarioPle'));
const LibroMayorPle        = lazy(() => import('./pages/LibroMayorPle'));
const BalanceComprobacionPle = lazy(() => import('./pages/BalanceComprobacionPle'));
const CajaYBancosPle       = lazy(() => import('./pages/CajaYBancosPle'));
const Settings             = lazy(() => import('./pages/Settings'));
const AiAssistant          = lazy(() => import('./components/AiAssistant'));
const CuentasPorPagar      = lazy(() => import('./pages/CuentasPorPagar'));
const CuentasPorCobrar     = lazy(() => import('./pages/CuentasPorCobrar'));
const BankAccounts         = lazy(() => import('./pages/BankAccounts'));
const TransactionInbox     = lazy(() => import('./pages/TransactionInbox'));
const Agentes              = lazy(() => import('./pages/Agentes'));
const Detracciones         = lazy(() => import('./pages/Detracciones'));
const Loans                = lazy(() => import('./pages/Loans'));
const AsientosAutomaticos  = lazy(() => import('./pages/AsientosAutomaticos'));
const EmailPreview         = lazy(() => import('./pages/EmailPreview'));
const EmprendedorDashboard = lazy(() => import('./pages/EmprendedorDashboard'));
const DesignSystem         = lazy(() => import('./pages/DesignSystem'));
const TrialExpiredScreen   = lazy(() => import('./pages/TrialExpiredScreen'));
const Billing              = lazy(() => import('./pages/Billing'));
const Upgrade              = lazy(() => import('./pages/Upgrade'));
const AdminPanel           = lazy(() => import('./pages/AdminPanel'));
const TeamSettings         = lazy(() => import('./pages/TeamSettings'));
const CodigoEducativo      = lazy(() => import('./pages/CodigoEducativo'));
const AdminCodigosEducativos = lazy(() => import('./pages/AdminCodigosEducativos'));
const AceptarInvitacion    = lazy(() => import('./pages/AceptarInvitacion'));

// ── Suspense fallback — full-screen spinner shown while a chunk loads ─────────
function LazySpinner() {
  return (
    <div className="flex items-center justify-center h-screen bg-[#060D1A]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400" />
    </div>
  );
}

// Module-level dedup: SIGNED_IN fires multiple times per signup (token refresh,
// cross-tab storage events, Supabase SDK internals). Track which user IDs have
// already triggered registration emails in this page session.
const _registrationEmailsFiredFor = new Set<string>();

/**
 * Fire-and-forget: send registration emails on first login.
 * Sends waitlist-pending to user + notify-admin to hola@tryquipus.com.
 * beta-welcome is sent only when admin approves (api/admin-approve.ts).
 * Called only when created_at ≈ last_sign_in_at (< 10 s), i.e. brand-new accounts.
 * Never throws — all errors are logged, never surfaced to the user.
 */
async function sendRegistrationEmails(email: string, accessToken: string): Promise<void> {
  const nombre = email.split('@')[0];

  const authHeaders = {
    'Content-Type':  'application/json',
    'Authorization': `Bearer ${accessToken}`,
  };

  const results = await Promise.allSettled([
    // Waitlist-pending — notifica que la solicitud fue recibida y está pendiente de aprobación.
    // beta-welcome se manda solo cuando admin aprueba (api/admin-approve.ts).
    fetch('/api/send-email', {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        template:  'waitlist-pending',
        to:        email,
        subject:   'Tu solicitud fue recibida — TryQuiPus',
        variables: { nombre },
      }),
    }).then(async r => {
      const json = await r.json().catch(() => null);
      if (!r.ok) console.error('[sendRegistrationEmails] waitlist-pending failed', r.status, json);
      else console.log('[sendRegistrationEmails] waitlist-pending sent, id:', (json as { id?: string })?.id);
      return json;
    }),
    // Notify admin — registro directo en app.tryquipus.com (sin auth headers, solo CORS origin)
    fetch('/api/notify-admin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, source: 'direct' }),
    }).then(async r => {
      const json = await r.json().catch(() => null);
      if (!r.ok) console.error('[sendRegistrationEmails] notify-admin failed', r.status, json);
      else console.log('[sendRegistrationEmails] admin notified of direct signup');
      return json;
    }),
  ]);

  results.forEach((r, i) => {
    if (r.status === 'rejected') {
      console.error(`[sendRegistrationEmails] request ${i} rejected:`, r.reason);
    }
  });
}

const ADMIN_EMAIL = 'hola@tryquipus.com';

/** Blocks access while profiles.plan = 'waitlist' (pending admin approval). */
function WaitlistGate({ children }: { children: ReactNode }) {
  const { plan, loading } = useUserRole();
  // Fail-closed: mientras el profile no terminó de cargar no sabemos el plan
  // real todavía — no hay que asumir acceso mientras tanto. `loading` pasa a
  // false recién cuando `plan` ya refleja el valor confirmado de `profiles`
  // (UserRoleContext.fetchProfile()).
  if (loading) return <LazySpinner />;
  if (plan === 'waitlist') return <WaitlistPendingScreen />;
  return <>{children}</>;
}

/** Renders TrialExpiredScreen when the subscription trial has lapsed. */
function TrialGate({ children }: { children: ReactNode }) {
  const { isTrialExpired, subscriptionLoading } = useCompanyContext();
  const { pathname } = useLocation();
  // Fail-closed: mientras el fetch de subscriptions no resolvió, no sabemos
  // todavía si hay fila real — no hay que asumir acceso mientras tanto.
  if (subscriptionLoading) return <LazySpinner />;
  // PAY-2: /billing debe ser alcanzable con el trial vencido — es justamente
  // donde el usuario paga para salir del gate. /codigo-educativo también:
  // un estudiante con trial vencido necesita poder activar su código.
  // /aceptar-invitacion también: un asistente invitado a otra firma no
  // debería quedar bloqueado por el trial vencido de SU cuenta al aceptar.
  // Cualquier otra ruta se bloquea.
  if (isTrialExpired && pathname !== '/billing' && pathname !== '/codigo-educativo'
    && pathname !== '/aceptar-invitacion') {
    return (
      <Suspense fallback={<LazySpinner />}>
        <TrialExpiredScreen />
      </Suspense>
    );
  }
  return <>{children}</>;
}

/**
 * Bloquea /cierre, /asientos-automaticos, /audit según plan_limits — antes
 * ninguna de las 3 rutas tenía guard alguno (fix 2026-08-16, PASO 3).
 * Fail-closed: mientras planLimits no cargó, no se asume acceso.
 */
function PlanFeatureGate({
  feature,
  children,
}: {
  feature: 'cierre_mensual' | 'asientos_automaticos' | 'audit_export';
  children: ReactNode;
}) {
  const { planLimits, planLimitsLoading } = useCompanyContext();
  if (planLimitsLoading) return <LazySpinner />;
  const allowed =
    feature === 'cierre_mensual'       ? (planLimits?.cierre_mensual_mes ?? 0) > 0 :
    feature === 'asientos_automaticos' ? (planLimits?.asientos_automaticos_mes ?? 0) > 0 :
    (planLimits?.audit_export_enabled ?? false);
  if (!allowed) return <Navigate to={`/upgrade?feature=${feature}`} replace />;
  return <>{children}</>;
}

export default function App() {
  const navigate = useNavigate();
  // Use a ref so the effect never needs navigate in its deps array — prevents
  // the subscription from tearing down and re-creating on every render.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const [session, setSession] = useState<Session | null | undefined>(undefined); // undefined = loading
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  // Derive role from the already-resolved session — no extra network call needed.
  const _meta = session?.user?.user_metadata as Record<string, unknown> | undefined;
  const sessionRole = _meta?.role === 'contador' || _meta?.role === 'emprendedor'
    ? (_meta.role as 'contador' | 'emprendedor')
    : null;
  const { viewMode: effectiveRole } = useUserRole();

  useEffect(() => {
    // Get current session immediately
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });
    // Listen for auth changes (login / logout / password reset)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (_event === 'PASSWORD_RECOVERY') {
        if (session?.user) {
          const uid = session.user.id;
          void logAuditEvent(supabase, uid, uid, {
            action: 'password_recovery',
            entityType: 'session',
            entityId: uid,
            changesJson: { timestamp: new Date().toISOString() },
          });
        }
        // Show update-password form instead of dashboard
        setPasswordRecovery(true);
        setSession(session);
        return;
      }
      if (_event === 'SIGNED_OUT') {
        // El evento 'logout' ya no se escribe acá: cuando llega SIGNED_OUT la sesión
        // fue borrada y el insert saldría sin JWT (RLS lo rechaza). Lo escribe
        // signOutWithAudit() ANTES de signOut(), con motivo y scope. Un SIGNED_OUT
        // sin ese registro previo es involuntario (refresh fallido o sesión revocada).
        clearLoginAuditMarker();
        localStorage.removeItem('quipus_company');
        localStorage.removeItem('quipus_filter_periodo');
        // L-4 (Security Audit 2026-09-28): quipus_active_user (UserContext.tsx)
        // no se limpiaba en logout — nombre/email sobrevivían en localStorage
        // en máquinas compartidas.
        localStorage.removeItem('quipus_active_user');
      }
      if (_event === 'USER_UPDATED') {
        setPasswordRecovery(false);
      }
      setSession(session);
      // Only redirect on an explicit user sign-in, not on token refreshes.
      // TOKEN_REFRESHED also fires SIGNED_IN in some Supabase versions; cross-window
      // storage events can also trigger SIGNED_IN when another tab refreshes the token.
      // Guard: only redirect if the user is NOT already on an authenticated route (BUG-8).
      if (_event === 'SIGNED_IN' && !session?.user) return;
      if (_event === 'SIGNED_IN') {
        const user = session!.user;
        // supabase-js también emite SIGNED_IN al recuperar el foco de la pestaña:
        // solo auditamos el primero desde el último logout.
        if (shouldAuditLogin(user.id)) {
          void logAuditEvent(supabase, user.id, user.id, {
            action: 'login',
            entityType: 'session',
            entityId: user.id,
            changesJson: { email: user.email, timestamp: new Date().toISOString() },
          });
        }
        // Detect new registration: created_at ≈ last_sign_in_at (within 10 s).
        // Returning users have last_sign_in_at > created_at by more than a few seconds.
        if (user.email && user.created_at && user.last_sign_in_at) {
          const createdMs   = new Date(user.created_at).getTime();
          const lastSignIn  = new Date(user.last_sign_in_at).getTime();
          if (Math.abs(lastSignIn - createdMs) < 10_000 &&
              !_registrationEmailsFiredFor.has(user.id)) {
            _registrationEmailsFiredFor.add(user.id);
            const token = session!.access_token;
            void sendRegistrationEmails(user.email, token);
          }
        }

        const p = window.location.pathname;
        // Inverted guard (BUG-NAV): el whitelist de rutas autenticadas era
        // incompleto (faltaban /tributaria, /pdt-621, /cxp, /cxc, /journals,
        // /pyg, /export, /impuestos, /detracciones, /sumas-saldos, /audit,
        // /libros-ple/*, /biblioteca/*, /settings, /ai-assistant, /inbox,
        // /categorizer, /cierre, /bank-accounts...). En cada SIGNED_IN
        // disparado por TOKEN_REFRESHED o cross-tab sync, el usuario era
        // expulsado a /contador/mi-negocio/welcome.
        // Blacklist solo lista los puntos de entrada no-autenticados conocidos.
        // Cualquier otra ruta = usuario ya está donde quería estar.
        const onUnauthEntryRoute =
          p === '/login' ||
          p === '/beta-expired' ||
          p === '/role-selector';
        if (onUnauthEntryRoute) {
          if (!session) return;
          // Genuine new login from /login or unauthenticated entry point →
          // Route by role: emprendedor → /mi-negocio, contador/admin → /contador
          // Fall back to /welcome for onboarding (no role set yet).
          showToast(`Bienvenido/a, ${session.user.email ?? 'usuario'}`, 'success', 3000);
          const loginRole = (session.user.user_metadata as Record<string, unknown>)?.role;
          if (loginRole === 'emprendedor') {
            navigateRef.current('/');
          } else if (loginRole === 'contador' || loginRole === 'admin') {
            navigateRef.current('/contador');
          } else {
            navigateRef.current('/welcome');
          }
        }
        // Already on an authenticated route (token refresh, cross-window event,
        // re-login on expired session within the app) → do nothing. Session
        // state has already been updated via setSession above; the user stays
        // exactly where they were (e.g. /tributaria, /pdt-621, /cxp).
      }
    });
    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // stable: navigateRef never changes, supabase is a module singleton

  // Trial / access gating is handled by a SINGLE source of truth: the
  // `subscriptions` row, evaluated in CompanyContext → <TrialGate> below.
  // The legacy profiles.beta_expires_at gate has been removed.

  // Public routes — accessible without auth
  if (window.location.pathname === '/terms') return <LangProvider><BetaTerms /></LangProvider>;
  if (window.location.pathname === '/beta-expired') return <BetaExpired />;
  if (window.location.pathname === '/politica-privacidad') return <LangProvider><PrivacyPolicy /></LangProvider>;

  // Loading state — session resolving
  if (session === undefined) {
    return (
      <div className="min-h-screen bg-[#0b1628] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Not logged in → show Login page
  if (session === null) {
    return (
      <Suspense fallback={<LazySpinner />}>
        <Login />
      </Suspense>
    );
  }

  // Password reset flow — show update-password form
  if (passwordRecovery) {
    return (
      <Suspense fallback={<LazySpinner />}>
        <Login view="update_password" />
      </Suspense>
    );
  }

  // Admin bypass — hola@tryquipus.com salta todos los gates
  if (session.user.email === ADMIN_EMAIL) {
    return (
      <Suspense fallback={<LazySpinner />}>
        {window.location.pathname === '/admin/codigos-educativos'
          ? <AdminCodigosEducativos />
          : <AdminPanel />}
      </Suspense>
    );
  }

  // No role set → onboarding — except admin user accessing /admin directly
  if (sessionRole === null && window.location.pathname !== '/admin') {
    return <RoleSelector />;
  }

  // Logged in → full app
  return (
    <LangProvider>
    <GlobalLoadingProvider>
    <UserProvider>
    <CompanyProvider userId={session.user.id}>
    <WaitlistGate>
    <TrialGate>
    <SyncProvider>
    <AlertsProvider>
    <UploadSessionProvider>
      <ErrorBoundary fallbackTitle="Error en la aplicación">
      <Suspense fallback={<LazySpinner />}>
      <Routes>
        {/* Welcome / onboarding — full-screen, no sidebar */}
        <Route path="/welcome" element={<Welcome />} />
        {/* Acceso educativo — full-screen, alcanzable con trial vencido (ver TrialGate) */}
        <Route path="/codigo-educativo" element={<CodigoEducativo />} />
        {/* Aceptar invitación de equipo — full-screen, alcanzable con trial vencido (ver TrialGate) */}
        <Route path="/aceptar-invitacion" element={<AceptarInvitacion />} />

        <Route element={<Layout />}>
          {/* §18 root swap: / = Mi Negocio, Setup → /perfil-empresa */}
          <Route path="/"               element={<EmprendedorDashboard />} />
          <Route path="/mi-negocio"     element={<Navigate to="/" replace />} />
          <Route path="/perfil-empresa" element={<Setup />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/upgrade" element={<Upgrade />} />
          <Route path="/upload" element={<Upload />} />
          <Route path="/bank-accounts" element={<BankAccounts />} />
          <Route path="/bank-inbox"      element={<BankInbox />} />
          <Route path="/inbox"           element={<TransactionInbox />} />
          <Route path="/agentes"         element={<Agentes />} />
          {/* §18 /inbox-fallidos → /cargas-fallidas */}
          <Route path="/cargas-fallidas" element={<InboxFallidos />} />
          <Route path="/inbox-fallidos"  element={<Navigate to="/cargas-fallidas" replace />} />
          <Route path="/loans"           element={<Loans />} />
          <Route path="/asientos-automaticos" element={
            <PlanFeatureGate feature="asientos_automaticos"><AsientosAutomaticos /></PlanFeatureGate>
          } />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/balance" element={<Balance />} />
          <Route path="/pyg" element={<PyG />} />
          <Route path="/flujo"  element={<FlujoCaja />} />
          <Route path="/cxp"    element={<CuentasPorPagar />} />
          <Route path="/cxc"    element={<CuentasPorCobrar />} />
          <Route path="/cierre" element={
            <PlanFeatureGate feature="cierre_mensual"><CierreDual /></PlanFeatureGate>
          } />
          <Route path="/journals" element={<JournalManual />} />
          <Route path="/libro-diario" element={<LibroDiario />} />
          <Route path="/libro-mayor"    element={<LibroMayor />} />
          <Route path="/sumas-saldos"  element={<SumasSaldos />} />
          <Route path="/tributaria" element={<Tributaria />} />
          <Route path="/compras" element={<Compras />} />
          <Route path="/ventas" element={<Ventas />} />
          <Route path="/impuestos"     element={<ImpuestosIGV />} />
          {/* §18 /pdt-621 → /declaraciones */}
          <Route path="/declaraciones" element={<PDT621 />} />
          <Route path="/pdt-621"       element={<Navigate to="/declaraciones" replace />} />
          <Route path="/detracciones" element={<Detracciones />} />
          {/* §18 /categorizer → /maestro-fiscal */}
          <Route path="/maestro-fiscal" element={<CategorizadorRuc />} />
          <Route path="/categorizer"    element={<Navigate to="/maestro-fiscal" replace />} />
          <Route path="/audit" element={
            <PlanFeatureGate feature="audit_export"><Audit /></PlanFeatureGate>
          } />
          {/* §18 /libros-ple/* → /libros-pe/* */}
          <Route path="/libros-pe/diario"               element={<LibroDiarioPle />} />
          <Route path="/libros-pe/libro-mayor"          element={<LibroMayorPle />} />
          <Route path="/libros-pe/balance-comprobacion" element={<BalanceComprobacionPle />} />
          <Route path="/libros-pe/caja-bancos"          element={<CajaYBancosPle />} />
          <Route path="/libros-ple/diario-51"            element={<Navigate to="/libros-pe/diario" replace />} />
          <Route path="/libros-ple/mayor-61"             element={<Navigate to="/libros-pe/libro-mayor" replace />} />
          <Route path="/libros-ple/balance-comprobacion" element={<Navigate to="/libros-pe/balance-comprobacion" replace />} />
          <Route path="/libros-ple/caja-bancos"          element={<Navigate to="/libros-pe/caja-bancos" replace />} />

          <Route path="/biblioteca" element={<Biblioteca />} />
          {/* Biblioteca única: las rutas anteriores redirigen a su sección */}
          <Route path="/biblioteca/legal" element={<Navigate to="/biblioteca#marco-legal" replace />} />
          <Route path="/biblioteca/sunat" element={<Navigate to="/biblioteca#marco-legal" replace />} />
          <Route path="/biblioteca/normas" element={<Navigate to="/biblioteca#marco-legal" replace />} />

          <Route path="/settings"       element={<Settings />} />
          <Route path="/team-settings"  element={<TeamSettings />} />
          <Route path="/ai-assistant" element={<AiAssistant />} />
          {/* Contador-only routes — emprendedor is redirected to /dashboard */}
          {import.meta.env.DEV && (
            <Route path="/email-preview" element={<EmailPreview />} />
          )}
          <Route path="/contador" element={
            effectiveRole === 'emprendedor' ? <Navigate to="/dashboard" replace /> : <AccountantConsole />
          } />
          <Route path="/contador/clientes" element={
            effectiveRole === 'emprendedor' ? <Navigate to="/dashboard" replace /> : <ClientesView />
          } />
          {import.meta.env.DEV && (
            <Route path="/design-system" element={<DesignSystem />} />
          )}
          {/* Defensa en profundidad: el bypass de línea 337 ya intercepta a
              ADMIN_EMAIL antes de llegar aquí para cualquier ruta, así que la
              rama <AdminPanel/> abajo es hoy inalcanzable en la práctica — se
              deja explícita para que el router nunca dependa únicamente del
              useEffect interno de AdminPanel.tsx/AdminCodigosEducativos.tsx
              (fix #13, auditoría 2026-08-15). */}
          <Route path="/admin" element={
            session.user.email === ADMIN_EMAIL ? <AdminPanel /> : <Navigate to="/welcome" replace />
          } />
          <Route path="/admin/codigos-educativos" element={
            session.user.email === ADMIN_EMAIL
              ? <AdminCodigosEducativos />
              : <Navigate to="/welcome" replace />
          } />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Routes>
      </Suspense>
      </ErrorBoundary>
    </UploadSessionProvider>
    </AlertsProvider>
    </SyncProvider>
    </TrialGate>
    </WaitlistGate>
    </CompanyProvider>
    </UserProvider>
    </GlobalLoadingProvider>
    </LangProvider>
  );
}
